import React from 'react';
import { IonContent, IonText, IonButton, IonIcon, IonGrid, IonRow, IonCol, IonCard, IonCardContent, IonBadge, IonSpinner } from '@ionic/react';
import { shieldHalfOutline } from 'ionicons/icons';
import { useInRouterContext, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts';
import mobileStyles from '../pages/unauthenticated/NotFound.module.scss';
import desktopStyles from '../desktop/pages/unauthenticated/DesktopErrorPage.module.scss';
import { useTranslation } from 'react-i18next';

const IS_DESKTOP = import.meta.env.VITE_PLATFORM === 'desktop';

interface AccessDeniedPageProps {
  /** Still working out whether the role is allowed: show a spinner, never the page */
  checking?: boolean;
  /** For a page inside a modal (the softphone): the only button, instead of Back and Home */
  onClose?: () => void;
}

interface ViewProps extends AccessDeniedPageProps {
  /** Missing when there is no earlier page inside the app (reload or typed address): Back is then not shown */
  goBack?: () => void;
  goHome: () => void;
}

const View: React.FC<ViewProps> = ({ checking = false, onClose, goBack, goHome }) => {
  const { t } = useTranslation();

  if (IS_DESKTOP) {
    const styles = desktopStyles;
    return (
      <IonContent className={styles.content}>
        <div className={styles.shell}>
          {checking ? (
            <IonSpinner name="dots" />
          ) : (
            <div className={styles.card}>
              <div className={`${styles.badge} ${styles.badgeDanger}`}>403</div>
              <div className={`${styles.iconWrap} ${styles.iconWrapDanger}`}>
                <IonIcon icon={shieldHalfOutline} className={styles.icon} />
              </div>

              <h1 className={styles.title}>{t('components.AccessDeniedPage.accessDenied')}</h1>
              <p className={styles.subtitle}>{t('components.AccessDeniedPage.yourAccountDoesNotHaveAccess')}</p>
              <p className={styles.detail}>{t('components.AccessDeniedPage.ifYouThinkThisIsA')}</p>

              <div className={styles.actions}>
                {onClose ? (
                  <button type="button" className={styles.secondaryBtn} onClick={onClose}>{t('components.AccessDeniedPage.close')}</button>
                ) : (
                  <>
                    {goBack && <button type="button" className={styles.secondaryBtn} onClick={goBack}>{t('components.AccessDeniedPage.back')}</button>}
                    <button type="button" className={styles.primaryBtn} onClick={goHome}>{t('components.AccessDeniedPage.home')}</button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </IonContent>
    );
  }

  const styles = mobileStyles;
  return (
    <IonContent className={styles.content}>
      <IonGrid className={styles.shell}>
        <IonRow className={styles.shellRow}>
          <IonCol size="12" sizeMd="10" sizeLg="8" className={styles.shellCol}>
            {checking ? (
              <IonSpinner name="dots" />
            ) : (
              <IonCard className={styles.card}>
                <IonCardContent>
                  <IonBadge className={`${styles.badge} ${styles.badgeDanger}`}>403</IonBadge>

                  <IonButton fill="clear" className={`${styles.iconWrap} ${styles.iconWrapDanger}`} aria-label={t('components.AccessDeniedPage.accessDenied')}>
                    <IonIcon icon={shieldHalfOutline} className={styles.icon} />
                  </IonButton>

                  <IonText className={styles.title}>{t('components.AccessDeniedPage.accessDenied')}</IonText>
                  <IonText className={styles.subtitle}>{t('components.AccessDeniedPage.yourAccountDoesNotHaveAccess')}</IonText>
                  <IonText className={styles.detail}>{t('components.AccessDeniedPage.ifYouThinkThisIsA')}</IonText>

                  <IonRow className={styles.actions}>
                    {onClose ? (
                      <IonCol size="auto" className={styles.actionCol}>
                        <IonButton fill="clear" className={styles.secondaryBtn} onClick={onClose}>{t('components.AccessDeniedPage.close')}</IonButton>
                      </IonCol>
                    ) : (
                      <>
                        {goBack && (
                          <IonCol size="auto" className={styles.actionCol}>
                            <IonButton fill="clear" className={styles.secondaryBtn} onClick={goBack}>{t('components.AccessDeniedPage.back')}</IonButton>
                          </IonCol>
                        )}
                        <IonCol size="auto" className={styles.actionCol}>
                          <IonButton fill="clear" className={styles.primaryBtn} onClick={goHome}>{t('components.AccessDeniedPage.home')}</IonButton>
                        </IonCol>
                      </>
                    )}
                  </IonRow>
                </IonCardContent>
              </IonCard>
            )}
          </IonCol>
        </IonRow>
      </IonGrid>
    </IonContent>
  );
};

// Home goes to the dashboard through the app's router. Back returns to the page you came from, only when you got here inside the app.
const RoutedView: React.FC<AccessDeniedPageProps> = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authState } = useAuth();
  const homePath = authState?.isLoggedIn ? '/tabs/dashboard' : '/login';
  const goHome = () => navigate(homePath, { replace: true });
  // A reload or typed address starts the app fresh (key 'default'), so it does not know the page before: no Back then, only Home
  const goBack = location.key !== 'default' ? () => navigate(-1) : undefined;
  return <View {...props} goBack={goBack} goHome={goHome} />;
};

/**
 * The whole page for an account whose role is not allowed to use it (or while that is being checked), in the same look as the
 * page-not-found screen: only the message, none of the page's own buttons, menus, sheets or data. Use it with useRoleAccess:
 *   const { access } = useRoleAccess(SOME_ROLES);
 *   if (access !== 'allowed') return <AccessDeniedPage checking={access === 'checking'} />;
 * Inside the softphone, which sits outside the router, pass onClose: Back and Home need the router.
 */
const AccessDeniedPage: React.FC<AccessDeniedPageProps> = (props) => {
  const inRouter = useInRouterContext();
  if (inRouter) return <RoutedView {...props} />;
  return <View {...props} goHome={() => window.history.back()} />;
};

export default AccessDeniedPage;
